import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import type { CaptionPositionKey } from '../hooks/use-active-caption';
import { getCaptionPositionClass } from '../hooks/use-active-caption';

const FONT_SIZE_BASE_RATIO = 0.06;
const FONT_SIZE_MIN_PX = 12;
const FONT_SIZE_MAX_PX = 36;
const TEXT_OUTLINE_COLOR = 'black';
const TEXT_SHADOW_COLOR = 'rgba(0, 0, 0, 0.8)';

export type CaptionOverlayViewProps = {
  visible: boolean;
  text: string | null;
  words: string[];
  activeWordIndex: number;
  highlightColor: string;
  fontFamily: string;
  fontWeight: string;
  fontScale: number;
  captionsPosition: CaptionPositionKey;
  controlsBarVisible?: boolean;
};

export const CaptionOverlayView = ({
  visible,
  text,
  words,
  activeWordIndex,
  highlightColor,
  fontFamily,
  fontWeight,
  fontScale,
  captionsPosition,
  controlsBarVisible = true,
}: CaptionOverlayViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const positionClass = getCaptionPositionClass(
    captionsPosition,
    controlsBarVisible,
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number;
    const resizeObserver = new ResizeObserver(entries => {
      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
    });

    resizeObserver.observe(container);
    setContainerWidth(container.offsetWidth);

    return () => {
      resizeObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const baseFontSize = containerWidth * FONT_SIZE_BASE_RATIO * fontScale;
  const adaptiveFontSize = Math.max(
    FONT_SIZE_MIN_PX,
    Math.min(FONT_SIZE_MAX_PX, baseFontSize),
  );

  const showCaption = visible && text;

  const baseTextStyle: React.CSSProperties = {
    fontFamily,
    fontWeight,
    fontSize: `${adaptiveFontSize}px`,
    WebkitTextStroke: `2px ${TEXT_OUTLINE_COLOR}`,
    paintOrder: 'stroke fill',
    textShadow: `1px 1px 0 ${TEXT_OUTLINE_COLOR}, -1px -1px 0 ${TEXT_OUTLINE_COLOR}, 1px -1px 0 ${TEXT_OUTLINE_COLOR}, -1px 1px 0 ${TEXT_OUTLINE_COLOR}, 2px 2px 4px ${TEXT_SHADOW_COLOR}`,
  };

  return (
    <div
      ref={containerRef}
      className={cx(
        'pointer-events-none absolute left-0 right-0 z-20 flex justify-center px-4',
        positionClass,
      )}
    >
      {showCaption && (
        <p
          className="m-0 max-w-[90%] text-center leading-tight text-white"
          style={baseTextStyle}
        >
          {words.map((word, index) => (
            <span
              key={`${word}-${index}`}
              style={{
                color: index === activeWordIndex ? highlightColor : undefined,
              }}
            >
              {word}
              {index < words.length - 1 ? ' ' : ''}
            </span>
          ))}
        </p>
      )}
    </div>
  );
};
